import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { FINANCE_OVERVIEW_PAGE_SIZE, financeOverviewQueryKey } from "../config";
import {
  fetchFinancePayableOverview,
  fetchFinancePayableOverviewExport,
  fetchFinanceReceivableOverview,
  fetchFinanceReceivableOverviewExport,
} from "../repo/financeOverviewRepo";
import {
  financePayableOverviewSearchParams,
  financeReceivableOverviewSearchParams,
  isFinanceOverviewCandidateLimitExceeded,
  isFinanceOverviewExportLimitExceeded,
  readFinancePayableOverviewSearchParams,
  readFinanceReceivableOverviewSearchParams,
} from "../service";
import type { FinanceOverviewExportFile } from "../types";
import { saveFinanceFile } from "./saveFinanceFile";

/** 导出没成功的原因：超过候选上限（提示加出团日期范围）或其他失败。 */
export type FinanceOverviewExportError = "limitExceeded" | "failed";

/**
 * 财务总览列表的共用状态：生效的筛选以地址为准（首次进入、刷新、页面开着时再从别处跳过来都按地址取数），
 * 筛选区里正在填的条件和页码留在页面上。点「查询」把填好的条件写进地址、回到第 1 页；重置清空地址；
 * 翻页不改条件。地址上的条件变了（例如从工作台跳过来），正在填的条件跟着换成地址上的、回到第 1 页。
 * 超过候选上限单独标出来，页面提示加出团日期范围，不当成普通失败。
 * 导出按地址上生效的条件（不是筛选区里还没点查询的）导出全部筛选行；导出没成功的提示只对当时的条件有效，
 * 条件变了就不再显示。
 */
export function useFinanceOverviewListRuntime<TFilters, TPage>(options: {
  name: string;
  readFilters: (params: URLSearchParams) => TFilters;
  writeFilters: (filters: TFilters) => URLSearchParams;
  fetchPage: (filters: TFilters, page: number, pageSize: number) => Promise<TPage>;
  exportFile: (filters: TFilters) => Promise<FinanceOverviewExportFile>;
}) {
  const { name, readFilters, writeFilters, fetchPage, exportFile } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [readFilters, searchParams]);
  const filtersKey = writeFilters(filters).toString();
  const [local, setLocal] = useState({ filtersKey, draft: filters, page: 1 });
  // 地址上的条件变了，本地正在填的条件和页码作废：换成地址上的条件、第 1 页。
  const current = local.filtersKey === filtersKey ? local : { filtersKey, draft: filters, page: 1 };
  const list = useQuery({
    queryKey: [...financeOverviewQueryKey, name, filtersKey, current.page],
    queryFn: () => fetchPage(filters, current.page, FINANCE_OVERVIEW_PAGE_SIZE),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const limitExceeded = list.isError && isFinanceOverviewCandidateLimitExceeded(list.error);
  const [exportState, setExportState] = useState<{
    exporting: boolean;
    filtersKey: string;
    error: FinanceOverviewExportError | null;
  }>({ exporting: false, filtersKey, error: null });
  const apply = (params: URLSearchParams, draft: TFilters) => {
    setLocal({ filtersKey: params.toString(), draft, page: 1 });
    setSearchParams(params);
  };
  return {
    draft: current.draft,
    setDraft(draft: TFilters) {
      setLocal({ ...current, draft });
    },
    /** 按填好的条件查：写进地址，回到第 1 页。 */
    search() {
      apply(writeFilters(current.draft), current.draft);
    },
    /** 清空条件和地址，回到第 1 页。 */
    reset() {
      const empty = new URLSearchParams();
      apply(empty, readFilters(empty));
    },
    page: current.page,
    setPage(page: number) {
      setLocal({ ...current, page });
    },
    pageSize: FINANCE_OVERVIEW_PAGE_SIZE,
    data: list.data ?? null,
    loading: list.isFetching,
    limitExceeded,
    failed: list.isError && !limitExceeded,
    refresh() {
      void list.refetch();
    },
    exporting: exportState.exporting,
    exportError: exportState.filtersKey === filtersKey ? exportState.error : null,
    /** 按地址上生效的条件导出全部筛选行，交给浏览器保存；超过候选上限单独标出。 */
    async exportExcel() {
      setExportState({ exporting: true, filtersKey, error: null });
      try {
        const file = await exportFile(filters);
        saveFinanceFile(file.blob, file.fileName);
        setExportState({ exporting: false, filtersKey, error: null });
      } catch (error) {
        setExportState({
          exporting: false,
          filtersKey,
          error: isFinanceOverviewExportLimitExceeded(error) ? "limitExceeded" : "failed",
        });
      }
    },
  };
}

/** 应收列表：全部有团编码的团，按页取，合计是全部筛选结果；筛选以地址为准。 */
export function useFinanceReceivableOverviewRuntime() {
  return useFinanceOverviewListRuntime({
    name: "receivables",
    readFilters: readFinanceReceivableOverviewSearchParams,
    writeFilters: financeReceivableOverviewSearchParams,
    fetchPage: fetchFinanceReceivableOverview,
    exportFile: fetchFinanceReceivableOverviewExport,
  });
}

/** 应付列表：有确认件或有请款的行程一行，按页取，合计是全部筛选结果；筛选以地址为准（工作台跳过来带「还有可请额度」）。 */
export function useFinancePayableOverviewRuntime() {
  return useFinanceOverviewListRuntime({
    name: "payables",
    readFilters: readFinancePayableOverviewSearchParams,
    writeFilters: financePayableOverviewSearchParams,
    fetchPage: fetchFinancePayableOverview,
    exportFile: fetchFinancePayableOverviewExport,
  });
}
